import React, { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

function useIsAdmin() {
  const { user, loading: authLoading } = useAuth();
  const [isAdmin, setIsAdmin] = useState(null); // null = loading
  useEffect(() => {
    let cancelled = false;
    if (authLoading) return;
    if (!user) { setIsAdmin(false); return; }
    (async () => {
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .eq("role", "admin")
        .maybeSingle();
      if (cancelled) return;
      setIsAdmin(!error && !!data);
    })();
    return () => { cancelled = true; };
  }, [user, authLoading]);
  return isAdmin;
}

function NotAuthorized({ lang }) {
  const t = (es, en) => (lang === "es" ? es : en);
  return (
    <div style={{
      background: "hsl(0 70% 95%)", color: "hsl(0 70% 30%)",
      border: "1px solid hsl(0 70% 80%)", padding: 16, borderRadius: 8,
      fontSize: 13, fontWeight: 600, textAlign: "center",
    }}>
      🔒 {t(
        "Solo administradores pueden acceder a las herramientas de diagnóstico.",
        "Only administrators can access diagnostic tools."
      )}
    </div>
  );
}

function AdminAuditLogPanel({ lang, refreshKey }) {
  const t = (es, en) => (lang === "es" ? es : en);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filterAction, setFilterAction] = useState("all");

  const load = async () => {
    setLoading(true); setError(null);
    try {
      let q = supabase
        .from("admin_action_audit_log")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(50);
      if (filterAction !== "all") q = q.eq("action", filterAction);
      const { data, error: e } = await q;
      if (e) throw e;
      setRows(data || []);
    } catch (e) {
      setError(e.message || String(e));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterAction, refreshKey]);

  return (
    <div style={{ background: "var(--bg2)", border: "1px solid var(--bd2)", borderRadius: "var(--r2)", padding: 16, marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, gap: 10, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700 }}>📜 {t("Auditoría de acciones de admin", "Admin action audit log")}</div>
          <div style={{ fontSize: 11, color: "var(--tx3)" }}>
            {t("Registro de quién ejecutó búsquedas y dry-runs.", "Record of who ran searches and dry-runs.")}
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <select value={filterAction} onChange={(e) => setFilterAction(e.target.value)}
            style={{ fontSize: 11, padding: "5px 8px", border: "1px solid var(--bd2)", borderRadius: 6, background: "var(--bg)", color: "var(--tx)" }}>
            <option value="all">{t("Todas", "All actions")}</option>
            <option value="inspect_user_provisioning">inspect_user_provisioning</option>
            <option value="test_handle_new_user_dryrun">test_handle_new_user_dryrun</option>
          </select>
          <button className="btn bg bs" onClick={load} disabled={loading} style={{ fontSize: 11 }}>
            {loading ? "…" : t("Recargar", "Reload")}
          </button>
        </div>
      </div>

      {error && (
        <div style={{ background: "hsl(0 70% 95%)", color: "hsl(0 70% 30%)", border: "1px solid hsl(0 70% 80%)", padding: 8, borderRadius: 6, fontSize: 11, marginBottom: 8 }}>
          ⚠️ {error}
        </div>
      )}

      {rows.length === 0 && !loading && !error && (
        <div style={{ color: "var(--tx3)", fontSize: 11, padding: 8 }}>{t("Sin entradas todavía.", "No entries yet.")}</div>
      )}

      {rows.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <table className="tbl" style={{ fontSize: 10 }}>
            <thead>
              <tr>
                <th>{t("Fecha", "Date")}</th>
                <th>Admin</th>
                <th>{t("Acción", "Action")}</th>
                <th>{t("Email objetivo", "Target email")}</th>
                <th>Status</th>
                <th>{t("Resumen", "Summary")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td style={{ color: "var(--tx3)", whiteSpace: "nowrap" }}>{new Date(r.created_at).toLocaleString()}</td>
                  <td style={{ fontSize: 10 }}>{r.admin_email || <code>{(r.admin_user_id || "").slice(0, 8)}…</code>}</td>
                  <td><code>{r.action}</code></td>
                  <td>{r.target_email || "—"}</td>
                  <td style={{ fontWeight: 600, color: r.status === "success" ? "hsl(140 60% 40%)" : "hsl(0 70% 50%)" }}>
                    {r.status === "success" ? "✅" : "⚠️"} {r.status}
                  </td>
                  <td style={{ color: "var(--tx3)", maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis" }}>
                    {r.error_message || (r.result_summary ? JSON.stringify(r.result_summary) : "—")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function UserInspector({ lang, onAfterAction }) {
  const t = (es, en) => (lang === "es" ? es : en);
  const isAdmin = useIsAdmin();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  // Defensa: si pierde permisos en runtime, limpiar todo dato sensible
  useEffect(() => {
    if (isAdmin === false) {
      setData(null);
      setError(null);
      setEmail("");
    }
  }, [isAdmin]);

  if (isAdmin === null) {
    return <div style={{ padding: 12, color: "var(--tx3)", fontSize: 11 }}>
      {t("Verificando permisos…", "Checking permissions…")}
    </div>;
  }
  if (isAdmin === false) {
    return <NotAuthorized lang={lang} />;
  }

  const search = async () => {
    if (!isAdmin) {
      setError(t("No autorizado: solo administradores.", "Not authorized: admins only."));
      setData(null);
      return;
    }
    setLoading(true); setError(null); setData(null);
    try {
      const { data: res, error: e } = await supabase.functions.invoke("admin-inspect-user", {
        body: { email },
      });
      if (e) throw e;
      if (res && (res as { error?: string }).error) throw new Error((res as { error: string }).error);
      setData(res);
      onAfterAction && onAfterAction();
    } catch (e) {
      setError(e.message || String(e));
      onAfterAction && onAfterAction();
    } finally { setLoading(false); }
  };

  const Badge = ({ ok, label }) => (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "3px 8px", borderRadius: 999, fontSize: 10, fontWeight: 700,
      background: ok ? "hsl(140 60% 92%)" : "hsl(0 70% 94%)",
      color: ok ? "hsl(140 60% 28%)" : "hsl(0 70% 38%)",
      border: `1px solid ${ok ? "hsl(140 60% 75%)" : "hsl(0 70% 80%)"}`,
    }}>{ok ? "✅" : "❌"} {label}</span>
  );

  return (
    <div style={{ background: "var(--bg2)", border: "1px solid var(--bd2)", borderRadius: "var(--r2)", padding: 16, marginBottom: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>
        🔎 {t("Inspeccionar usuario por email", "Inspect user by email")}
      </div>
      <div style={{ fontSize: 11, color: "var(--tx3)", marginBottom: 12 }}>
        {t(
          "Verifica si un usuario tiene profile, role, reward_points y referral_code, y muestra el último log de provisioning.",
          "Checks if a user has profile, role, reward_points, and referral_code, and shows the latest provisioning log."
        )}
      </div>
      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
        <input
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="user@example.com"
          onKeyDown={(e) => e.key === "Enter" && email && search()}
          style={{ flex: 1, fontSize: 12, padding: "7px 10px", border: "1px solid var(--bd2)", borderRadius: 6, background: "var(--bg)", color: "var(--tx)" }}
        />
        <button className="btn bp bs" onClick={search} disabled={loading || !email} style={{ fontSize: 12, padding: "8px 16px" }}>
          {loading ? t("Buscando…", "Searching…") : t("Buscar", "Search")}
        </button>
      </div>

      {error && (
        <div style={{ background: "hsl(0 70% 95%)", color: "hsl(0 70% 30%)", border: "1px solid hsl(0 70% 80%)", padding: 10, borderRadius: 8, fontSize: 12 }}>⚠️ {error}</div>
      )}

      {data && data.found === false && (
        <div style={{ background: "hsl(45 90% 95%)", color: "hsl(35 80% 30%)", border: "1px solid hsl(45 80% 75%)", padding: 10, borderRadius: 8, fontSize: 12 }}>
          ⚠️ {data.message}
        </div>
      )}

      {data && data.found && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ background: "var(--bg)", padding: 12, borderRadius: 8, fontSize: 11 }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>auth.users</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4, color: "var(--tx2)" }}>
              <div><b>email:</b> {data.auth_user?.email}</div>
              <div><b>provider:</b> {data.provider}</div>
              <div><b>created:</b> {data.auth_user?.created_at && new Date(data.auth_user.created_at).toLocaleString()}</div>
              <div><b>last_sign_in:</b> {data.auth_user?.last_sign_in_at ? new Date(data.auth_user.last_sign_in_at).toLocaleString() : "—"}</div>
              <div><b>email_confirmed:</b> {data.auth_user?.email_confirmed_at ? "✅" : "❌"}</div>
              <div><b>id:</b> <code style={{ fontSize: 10 }}>{data.auth_user?.id}</code></div>
            </div>
          </div>

          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <Badge ok={data.checks?.profile_ok} label="profile" />
            <Badge ok={data.checks?.role_ok} label="user_role" />
            <Badge ok={data.checks?.reward_points_ok} label="reward_points" />
            <Badge ok={data.checks?.referral_code_ok} label="referral_code" />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div style={{ background: "var(--bg)", padding: 10, borderRadius: 8, fontSize: 11 }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>profile</div>
              {data.profile ? (
                <div style={{ color: "var(--tx2)" }}>
                  <div>{data.profile.first_name} {data.profile.last_name}</div>
                  <div style={{ color: "var(--tx3)" }}>tier: {data.profile.referral_tier} · onboarding: {data.profile.onboarding_completed ? "✅" : "❌"}</div>
                  {data.profile.avatar_url && <div style={{ color: "var(--tx3)", fontSize: 10, wordBreak: "break-all" }}>avatar: {data.profile.avatar_url.slice(0, 50)}…</div>}
                </div>
              ) : <div style={{ color: "hsl(0 70% 50%)" }}>{t("No existe", "Missing")}</div>}
            </div>

            <div style={{ background: "var(--bg)", padding: 10, borderRadius: 8, fontSize: 11 }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>roles</div>
              {data.roles?.length ? data.roles.map((r, i) => (
                <div key={i} style={{ color: "var(--tx2)" }}>· <code>{r.role}</code></div>
              )) : <div style={{ color: "hsl(0 70% 50%)" }}>{t("Sin rol", "No role")}</div>}
            </div>

            <div style={{ background: "var(--bg)", padding: 10, borderRadius: 8, fontSize: 11 }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>reward_points</div>
              {data.reward_points ? (
                <div style={{ color: "var(--tx2)" }}>balance: <b>{data.reward_points.balance}</b> · earned: {data.reward_points.points_earned} · redeemed: {data.reward_points.points_redeemed}</div>
              ) : <div style={{ color: "hsl(0 70% 50%)" }}>{t("No existe", "Missing")}</div>}
            </div>

            <div style={{ background: "var(--bg)", padding: 10, borderRadius: 8, fontSize: 11 }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>referral_code</div>
              {data.referral_code ? (
                <div style={{ color: "var(--tx2)" }}>code: <code style={{ fontWeight: 700 }}>{data.referral_code.code}</code> · uses: {data.referral_code.uses_count}</div>
              ) : <div style={{ color: "hsl(0 70% 50%)" }}>{t("No existe", "Missing")}</div>}
            </div>
          </div>

          <div style={{ background: "var(--bg)", padding: 10, borderRadius: 8, fontSize: 11 }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>{t("Último log de provisioning", "Latest provisioning log")}</div>
            {data.last_log ? (
              <div style={{ color: "var(--tx2)" }}>
                <div>{new Date(data.last_log.created_at).toLocaleString()} · <code>{data.last_log.step}</code> · <b style={{ color: data.last_log.status === "success" ? "hsl(140 60% 40%)" : "hsl(0 70% 50%)" }}>{data.last_log.status}</b></div>
                {data.last_log.error_message && <div style={{ color: "hsl(0 70% 50%)", marginTop: 3 }}>{data.last_log.error_message}</div>}
                {data.last_log.metadata && <pre style={{ fontSize: 10, color: "var(--tx3)", margin: "4px 0 0", whiteSpace: "pre-wrap" }}>{JSON.stringify(data.last_log.metadata, null, 2)}</pre>}
              </div>
            ) : <div style={{ color: "var(--tx3)" }}>{t("Sin entradas en el log", "No log entries")}</div>}
          </div>

          {data.logs?.length > 1 && (
            <details style={{ fontSize: 10, color: "var(--tx3)" }}>
              <summary style={{ cursor: "pointer" }}>{t(`Ver historial completo (${data.logs.length})`, `View full history (${data.logs.length})`)}</summary>
              <table className="tbl" style={{ fontSize: 10, marginTop: 6 }}>
                <thead><tr><th>{t("Fecha", "Date")}</th><th>step</th><th>status</th><th>{t("Detalles", "Details")}</th></tr></thead>
                <tbody>
                  {data.logs.map((l) => (
                    <tr key={l.id}>
                      <td>{new Date(l.created_at).toLocaleString()}</td>
                      <td><code>{l.step}</code></td>
                      <td style={{ color: l.status === "success" ? "hsl(140 60% 40%)" : "hsl(0 70% 50%)", fontWeight: 600 }}>{l.status}</td>
                      <td style={{ color: "var(--tx3)" }}>{l.error_message || (l.metadata ? JSON.stringify(l.metadata) : "—")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

export function AuthDryRunPanel({ lang }) {
  const isAdmin = useIsAdmin();
  const [email, setEmail] = useState("test.google.user@example.com");
  const [fullName, setFullName] = useState("Maria Lopez Garcia");
  const [picture, setPicture] = useState("https://lh3.googleusercontent.com/a/test-avatar");
  const [provider, setProvider] = useState("google");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [logs, setLogs] = useState([]);
  const [auditRefresh, setAuditRefresh] = useState(0);

  const t = (es, en) => (lang === "es" ? es : en);

  // Defensa: si pierde permisos en runtime, purgar resultados sensibles
  useEffect(() => {
    if (isAdmin === false) {
      setResult(null);
      setLogs([]);
      setError(null);
    }
  }, [isAdmin]);

  const runTest = async () => {
    if (!isAdmin) {
      setError(t("No autorizado: solo administradores.", "Not authorized: admins only."));
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const { data, error: rpcError } = await supabase.functions.invoke("admin-test-dryrun", {
        body: { email, full_name: fullName, picture, provider },
      });
      if (rpcError) throw rpcError;
      if (data && (data as { error?: string }).error) throw new Error((data as { error: string }).error);
      setResult(data);

      // Cargar últimos logs reales del trigger
      const { data: logRows } = await supabase
        .from("auth_user_provisioning_log")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(10);
      setLogs(logRows || []);
      setAuditRefresh((n) => n + 1);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  const stepIcon = (status) => (status === "success" ? "✅" : status === "error" ? "❌" : "⚪");

  if (isAdmin === null) {
    return <div style={{ padding: 20, textAlign: "center", color: "var(--tx3)", fontSize: 12 }}>
      {t("Verificando permisos…", "Checking permissions…")}
    </div>;
  }
  if (isAdmin === false) {
    return <NotAuthorized lang={lang} />;
  }

  return (
    <div>
      <UserInspector lang={lang} onAfterAction={() => setAuditRefresh((n) => n + 1)} />
      <div style={{ background: "var(--bg2)", border: "1px solid var(--bd2)", borderRadius: "var(--r2)", padding: 16, marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>
          🧪 {t("Dry-run de handle_new_user", "handle_new_user dry-run")}
        </div>
        <div style={{ fontSize: 11, color: "var(--tx3)", marginBottom: 14 }}>
          {t(
            "Simula la creación de un usuario de Google. Inserta y revierte datos de prueba para validar profile + role + reward_points + referral_code.",
            "Simulates a Google user signup. Inserts and rolls back test data to validate profile + role + reward_points + referral_code."
          )}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
          <label style={{ fontSize: 11 }}>
            <div style={{ color: "var(--tx3)", marginBottom: 3, fontWeight: 600 }}>Email</div>
            <input value={email} onChange={(e) => setEmail(e.target.value)}
              style={{ width: "100%", fontSize: 11, padding: "6px 8px", border: "1px solid var(--bd2)", borderRadius: 6, background: "var(--bg)", color: "var(--tx)" }} />
          </label>
          <label style={{ fontSize: 11 }}>
            <div style={{ color: "var(--tx3)", marginBottom: 3, fontWeight: 600 }}>full_name (Google)</div>
            <input value={fullName} onChange={(e) => setFullName(e.target.value)}
              style={{ width: "100%", fontSize: 11, padding: "6px 8px", border: "1px solid var(--bd2)", borderRadius: 6, background: "var(--bg)", color: "var(--tx)" }} />
          </label>
          <label style={{ fontSize: 11 }}>
            <div style={{ color: "var(--tx3)", marginBottom: 3, fontWeight: 600 }}>picture URL</div>
            <input value={picture} onChange={(e) => setPicture(e.target.value)}
              style={{ width: "100%", fontSize: 11, padding: "6px 8px", border: "1px solid var(--bd2)", borderRadius: 6, background: "var(--bg)", color: "var(--tx)" }} />
          </label>
          <label style={{ fontSize: 11 }}>
            <div style={{ color: "var(--tx3)", marginBottom: 3, fontWeight: 600 }}>provider</div>
            <select value={provider} onChange={(e) => setProvider(e.target.value)}
              style={{ width: "100%", fontSize: 11, padding: "6px 8px", border: "1px solid var(--bd2)", borderRadius: 6, background: "var(--bg)", color: "var(--tx)" }}>
              <option value="google">google</option>
              <option value="email">email</option>
              <option value="github">github</option>
            </select>
          </label>
        </div>

        <button className="btn bp bs" onClick={runTest} disabled={loading}
          style={{ fontSize: 12, padding: "8px 16px" }}>
          {loading ? t("Ejecutando…", "Running…") : t("▶ Ejecutar dry-run", "▶ Run dry-run")}
        </button>
      </div>

      {error && (
        <div style={{ background: "hsl(0 70% 95%)", color: "hsl(0 70% 30%)", border: "1px solid hsl(0 70% 80%)", padding: 12, borderRadius: 8, marginBottom: 12, fontSize: 12 }}>
          ⚠️ {error}
        </div>
      )}

      {result && (
        <div style={{ background: "var(--bg2)", border: "1px solid var(--bd2)", borderRadius: "var(--r2)", padding: 16, marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 10 }}>
            {t("Resultado del dry-run", "Dry-run result")} · {result.cleaned_up ? `🧹 ${t("limpieza ok", "cleanup ok")}` : "⚠️ no cleanup"}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {(result.steps || []).map((s, i) => (
              <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "6px 10px", background: "var(--bg)", borderRadius: 6, fontSize: 11 }}>
                <span>{stepIcon(s.status)}</span>
                <div style={{ flex: 1 }}>
                  <b>{s.step}</b>
                  {s.error && <div style={{ color: "hsl(0 70% 50%)", marginTop: 2 }}>{s.error}</div>}
                  {s.data && (
                    <pre style={{ margin: "4px 0 0", fontSize: 10, color: "var(--tx3)", whiteSpace: "pre-wrap" }}>
                      {JSON.stringify(s.data, null, 2)}
                    </pre>
                  )}
                  {s.code && <div style={{ color: "var(--tx3)", marginTop: 2 }}>code: <code>{s.code}</code></div>}
                  {s.role && <div style={{ color: "var(--tx3)", marginTop: 2 }}>role: <code>{s.role}</code></div>}
                </div>
              </div>
            ))}
          </div>
          <details style={{ marginTop: 10, fontSize: 10, color: "var(--tx3)" }}>
            <summary style={{ cursor: "pointer" }}>{t("Ver JSON completo", "View raw JSON")}</summary>
            <pre style={{ background: "var(--bg)", padding: 10, borderRadius: 6, overflow: "auto", marginTop: 6 }}>{JSON.stringify(result, null, 2)}</pre>
          </details>
        </div>
      )}

      {logs.length > 0 && (
        <div style={{ background: "var(--bg2)", border: "1px solid var(--bd2)", borderRadius: "var(--r2)", padding: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 10 }}>
            📋 {t("Últimos 10 logs reales (auth_user_provisioning_log)", "Latest 10 real logs (auth_user_provisioning_log)")}
          </div>
          <table className="tbl" style={{ fontSize: 10 }}>
            <thead>
              <tr>
                <th>{t("Fecha", "Date")}</th>
                <th>Email</th>
                <th>Provider</th>
                <th>Step</th>
                <th>Status</th>
                <th>{t("Detalles", "Details")}</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.id}>
                  <td style={{ color: "var(--tx3)" }}>{new Date(log.created_at).toLocaleString()}</td>
                  <td>{log.email}</td>
                  <td>{log.provider}</td>
                  <td><code>{log.step}</code></td>
                  <td>
                    <span style={{ color: log.status === "success" ? "hsl(140 60% 40%)" : "hsl(0 70% 50%)", fontWeight: 600 }}>
                      {stepIcon(log.status)} {log.status}
                    </span>
                  </td>
                  <td style={{ color: "var(--tx3)" }}>{log.error_message || (log.metadata ? JSON.stringify(log.metadata) : "—")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AdminAuditLogPanel lang={lang} refreshKey={auditRefresh} />
    </div>
  );
}